import { Suspense, type ReactNode } from 'react';
import { ThemeProvider } from './ThemeProvider';
import { Toaster } from '../shared/ui/sonner';
import { ErrorBoundary } from '../platform/ErrorBoundary';
import { AuthProvider } from '../platform/auth/AuthContext';
import { PreferencesProvider } from '../features/preferences/PreferencesContext';
import { DataProvider } from '../contexts/DataContext';
import { BillingProvider } from '../contexts/BillingContext';
import { PresenceProvider } from '../contexts/PresenceContext';
import { RealtimeProvider } from '../contexts/RealtimeContext';
import { NotificationProvider } from '../features/notifications/NotificationContext';
import { MessagingProvider } from '../features/messages/MessagingContext';
import { EmailUnreadProvider } from '../features/email/EmailUnreadContext';
import { UploadProvider } from '../contexts/UploadContext';
import { ConfirmProvider } from '../shared/ui/confirm';
import { UploadProgressPanel } from '../platform/upload/UploadProgressPanel';
import { WHATS_NEW_ENABLED } from '../release-notes';
import { lazyChunk } from '../utils/lazyChunk';
import { WebSharedTheme } from '../shared-ui/web/WebSharedTheme';

const UpdateNotificationBanner = lazyChunk(() => import('../platform/realtime/UpdateNotificationBanner'), (m) => m.UpdateNotificationBanner);
const WhatsNewSheet = lazyChunk(() => import('../platform/realtime/WhatsNewSheet'), (m) => m.WhatsNewSheet);
const CookieConsent = lazyChunk(() => import('../platform/cookies/CookieConsent'), (m) => m.CookieConsent);
const PWAIconManager = lazyChunk(() => import('../platform/PWAIconManager'), (m) => m.PWAIconManager);

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="contract-group-theme">
        <WebSharedTheme>
        <AuthProvider>
          <PreferencesProvider>
          <BillingProvider>
            <DataProvider>
              <RealtimeProvider>
              <Suspense fallback={null}>
                <UpdateNotificationBanner />
                {WHATS_NEW_ENABLED && <WhatsNewSheet />}
              </Suspense>
              <NotificationProvider>
                <MessagingProvider>
                  <EmailUnreadProvider>
                    <PresenceProvider>
                      <UploadProvider>
                        <ConfirmProvider>
                          {children}
                          <UploadProgressPanel />
                        </ConfirmProvider>
                      </UploadProvider>
                    </PresenceProvider>
                  </EmailUnreadProvider>
                </MessagingProvider>
              </NotificationProvider>
              </RealtimeProvider>
            </DataProvider>
          </BillingProvider>
          </PreferencesProvider>
          <Suspense fallback={null}>
            <CookieConsent />
            <PWAIconManager />
          </Suspense>
        </AuthProvider>
        </WebSharedTheme>
        <Toaster />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

AppProviders.displayName = 'AppProviders';
